// Windy Hill name-number: keeps the $5 back name/number add-on lines tied to their shirt line.
// - add-on quantity always follows the shirt (parent) quantity
// - add-on lines with no shirt parent are removed
// - a shirt with Back Name / Back Number but no matching add-on line has that property cleared
import { CartLinesUpdateEvent } from '@shopify/events';

const ADDON_HANDLE = 'back-name-number-add-on';
const PROP_BY_VARIANT_TITLE = { Name: 'Back Name', Number: 'Back Number', 'Chest Name': 'Chest Name' };
const nativeFetch = window.fetch.bind(window);
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
let timer = null;
let running = false;
let rerun = false;

const post = (path, body) =>
  nativeFetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());

const getCart = () => nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

/** One pass of fixes. Returns true if the cart was changed. */
async function fixOnce(cart) {
  const items = cart.items || [];
  const byKey = new Map(items.map((i) => [i.key, i]));
  const isAddon = (i) => i.handle === ADDON_HANDLE;
  const updates = {};

  // add-ons: remove orphans and duplicates, sync quantity to the parent shirt
  const seen = new Set();
  for (const item of items.filter(isAddon)) {
    const parentKey = item.parent_relationship && item.parent_relationship.parent_key;
    const parent = parentKey ? byKey.get(parentKey) : null;
    const prop = PROP_BY_VARIANT_TITLE[item.variant_title];
    const dupKey = `${parentKey}|${item.variant_title}`;
    if (!parent || isAddon(parent) || !prop || !parent.properties || !parent.properties[prop] || seen.has(dupKey)) {
      updates[item.key] = 0;
    } else if ((seen.add(dupKey), item.quantity !== parent.quantity)) {
      updates[item.key] = parent.quantity;
    }
  }
  if (Object.keys(updates).length) {
    await post('cart/update.js', { updates });
    return true;
  }

  // shirts: clear Back Name / Back Number when the matching add-on line is gone
  for (const item of items) {
    if (isAddon(item) || !item.properties) continue;
    const children = items.filter(
      (c) => isAddon(c) && c.parent_relationship && c.parent_relationship.parent_key === item.key
    );
    const props = { ...item.properties };
    let changed = false;
    for (const [variantTitle, prop] of Object.entries(PROP_BY_VARIANT_TITLE)) {
      if (props[prop] && !children.some((c) => c.variant_title === variantTitle)) {
        props[prop] = ''; // an empty properties object is ignored by /cart/change.js, so blank it instead
        changed = true;
      }
    }
    if (changed) {
      await post('cart/change.js', { id: item.key, quantity: item.quantity, properties: props });
      return true; // keys changed, re-read the cart before doing more
    }
  }
  return false;
}

/** Ask every cart-items-component (drawer + cart page) and the cart icon to re-render. */
function refreshCartUi(cart) {
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: (cart.items || []).map((i) => ({ id: i.key, quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'wh-name-number', didError: false },
    });
  } catch (e) {
    if (/\/cart\/?$/.test(location.pathname)) location.reload();
  }
}

async function sync() {
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    let cart = await getCart();
    let changed = false;
    for (let pass = 0; pass < 6 && (await fixOnce(cart)); pass++) {
      changed = true;
      cart = await getCart();
    }
    if (changed) refreshCartUi(cart);
  } catch (e) {
    console.warn('[Windy Hill name-number] cart sync failed', e);
  } finally {
    running = false;
    if (rerun) {
      rerun = false;
      schedule();
    }
  }
}

function schedule(delay = 400) {
  clearTimeout(timer);
  timer = setTimeout(sync, delay);
}

// Re-check after any theme cart request (add/change/update/clear), on load and on back/forward.
window.fetch = function (input, init) {
  const url = typeof input === 'string' ? input : (input && input.url) || '';
  const promise = nativeFetch(input, init);
  if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
    promise.then(() => schedule(), () => schedule());
  }
  return promise;
};
window.addEventListener('pageshow', () => schedule(0));
schedule(0);
